import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import type { PaymentsResponse } from '@//app/api/commerce/payments/[id]/list';
import { mapPayment } from '@//utils/payments';
import { httpClientGetJson } from '../httpClient';
import {
  type ModifyPaymentBody,
  type ModifyPaymentResponse,
  PAYMENT_CACHE_KEY,
} from './useModifyPayment';

export const PAYMENTS_CACHE_KEY = 'NEXT_PUBLIC_ECOM_URL/payment/vehicle/assignments';

export interface UsePaymentsVariables {
  id: string;
  branchId: string;
}

interface UsePayments {
  variables: UsePaymentsVariables | undefined;
  initialData?: PaymentsResponse;
}

const usePayments = ({ variables, initialData }: UsePayments) => {
  const queryClient = useQueryClient();

  const query = useQuery<PaymentsResponse>({
    queryKey: [PAYMENTS_CACHE_KEY, variables?.id, variables?.branchId],
    queryFn: async () => {
      const url = `${process.env.NEXT_PUBLIC_ECOM_URL}/payment/${variables?.id}/list?branchId=${variables?.branchId}`;
      const result = await httpClientGetJson<PaymentsResponse>(url);

      return {
        ...result,
        response: result.response.map((payment) => mapPayment(payment)),
      };
    },
    initialData,
    staleTime: 0,
    refetchOnMount: 'always',
    enabled: !!variables?.id && !!variables?.branchId,
  });

  // Preheat the cache only when new data arrives
  useEffect(() => {
    if (query.data?.response && variables) {
      query.data.response.forEach((payment) => {
        const rr: ModifyPaymentResponse = {
          response: payment,
          successful: true,
        };

        const key: ModifyPaymentBody = {
          branchId: variables.branchId,
          downPayment: payment.downPayment.current,
          duration: payment.duration.current,
          financialOptionId: payment.financialOptionId,
        };

        queryClient.setQueryData(
          [PAYMENT_CACHE_KEY, variables.id, payment.financialOptionId, JSON.stringify(key)],
          rr,
        );
      });
    }
  }, [query.data, queryClient, variables]);

  return query;
};

export default usePayments;
